// ---------------------------------------------------------------------------
// ContactForm Renderer (editor key: ContactForm)
// ---------------------------------------------------------------------------
// Renders a minimal contact form with name, email, message, and a submit
// button. The form posts to a configurable `action` URL via POST so
// progressive-enhancement works without client JS. The submit `action`
// URL routes through the URL safelist; an empty / unsafe URL collapses
// the form into a non-submitting placeholder ("#") that still renders
// safely. Heading, submit label, and success message are display text
// only — they pass through the `text()` escaper, never `innerHTML`.
//
// Distinct from `lead-form` and `multi-step-form`: those are richer
// conversion surfaces with field configuration; this is the lightweight
// "Get in touch" surface the editor exposes under the ContactForm Puck
// component.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";
import { buildTurnstileWidget } from "../form-utils.js";

interface ContactFormProps {
  heading?: string;
  submitLabel?: string;
  successMessage?: string;
  action?: string;
}

function resolveString(value: unknown, fallback: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return fallback;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as ContactFormProps;

  const heading = resolveString(p.heading, "Get in touch");
  const submitLabel = resolveString(p.submitLabel, "Send message");
  const successMessage = resolveString(
    p.successMessage,
    "Thanks! We'll be in touch soon.",
  );
  const action = typeof p.action === "string" && p.action.trim().length > 0
    ? url(p.action.trim())
    : "#";

  const turnstileWidget = buildTurnstileWidget(ctx);
  const turnstileHtml = turnstileWidget ? `\n    ${turnstileWidget}` : "";

  return `<section class="ease-section ease-contact-form">
  <h2 class="ease-contact-form__heading">${text(heading)}</h2>
  <form class="ease-contact-form__form" method="post" action="${action}" data-success-message="${attr(successMessage)}">
    <label class="ease-contact-form__label">
      <span class="ease-contact-form__label-text">Name</span>
      <input class="ease-contact-form__input" type="text" name="name" autocomplete="name" required />
    </label>
    <label class="ease-contact-form__label">
      <span class="ease-contact-form__label-text">Email</span>
      <input class="ease-contact-form__input" type="email" name="email" autocomplete="email" required />
    </label>
    <label class="ease-contact-form__label">
      <span class="ease-contact-form__label-text">Message</span>
      <textarea class="ease-contact-form__input ease-contact-form__textarea" name="message" rows="4" required></textarea>
    </label>${turnstileHtml}
    <button class="ease-contact-form__submit" type="submit">${text(submitLabel)}</button>
  </form>
</section>`;
}

registerRenderer("contact-form", { render });
